import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SortIcon from '@mui/icons-material/Sort';
import {
  FormControl,
  OutlinedInput,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

import { track } from '@hbf/analytics';

import { AnalyticsEvent } from '@kamernet/core/Analytics/types';
import {
  RoomAdvertSortOptionID,
  RoomTypeID,
} from '@kamernet/core/ApiClient/Kamernet';
import { useIntl } from '@kamernet/core/Intl';
import { useMediaIsDesktop } from '@kamernet/utilities/Media/useMediaIsDesktop';

import { useFilterSelectors } from '@kamernet/modules/Listing/context/hooks/useFilterSelectors';
import { useListingSelectors } from '@kamernet/modules/Listing/context/hooks/useListingSelectors';
import { ListingsContext } from '@kamernet/modules/Listing/context/ListingsContext';
import { useFilterStateDispatcher } from '@kamernet/modules/Listing/hooks/useFilterStateDispatcher';
import {
  RoomAdvertSortOptionIDTranslations,
  RoomTypeIDPluralTranslations,
} from '@kamernet/modules/Listing/translations';
import { RoomAdvertSortOptionIDValues } from '@kamernet/modules/Listing/types';

import styles from './SortListings.module.css';

const SortListingsInternal = () => {
  const { listingsState } = ListingsContext.useContext();
  const cityName = useFilterSelectors().getCityName();
  const streetName = useFilterSelectors().getStreetName();
  const totalListingCount = useListingSelectors().getTotalListingsCount();
  const isMapView = useFilterSelectors().isMapView();
  const { filterStateDispatcher } = useFilterStateDispatcher();
  const listingTypes =
    listingsState.filterResult.filters.listingTypeIds || null;

  const { T } = useIntl();
  const isMobile = !useMediaIsDesktop();

  const handleSortChange = React.useCallback(
    (event: SelectChangeEvent<RoomAdvertSortOptionID>) => {
      track(AnalyticsEvent.SearchSortApplied);
      const newSortValue = event.target.value as RoomAdvertSortOptionID;
      filterStateDispatcher({
        ...listingsState.filterResult.filters,
        listingSortOptionId: newSortValue,
        pageNo: 1,
      });
    },
    [filterStateDispatcher, listingsState.filterResult.filters],
  );

  const renderValue = (value: unknown) => (
    <Typography variant={isMobile ? 'body3' : 'body2'}>
      {T(RoomAdvertSortOptionIDTranslations[value as RoomAdvertSortOptionID])}
    </Typography>
  );

  function getLocationName(street: string | null | undefined, city: string) {
    if (street) {
      return street;
    }
    if (city === 'NETHERLANDS') {
      return T('LBL_NETHERLANDS');
    }
    return city;
  }

  const pageTitle = (types: RoomTypeID[] | null): string => {
    const locationName = getLocationName(streetName, cityName);

    if (types && types.length === 1 && types[0] !== undefined) {
      const listingType = types[0];

      if (listingType === RoomTypeID.StudentResidence) {
        return T(
          'listings_page.listing_count.student_residence',
          totalListingCount,
          locationName,
        );
      }

      if (listingType in RoomTypeIDPluralTranslations) {
        return T(
          'LBL_SEARCHROOMS_HEADER',
          totalListingCount,
          T(RoomTypeIDPluralTranslations[listingType]),
          locationName,
        );
      }
    }

    return T(
      'listings_page.listing_count.generic',
      totalListingCount,
      locationName,
    );
  };

  return (
    <div className={isMapView ? styles.mapGrid : styles.root}>
      <Typography
        data-testid="listings-count"
        component="span"
        variant="subtitle2"
      >
        {pageTitle(listingTypes)}
      </Typography>

      <div className={styles.sortDropdown}>
        <FormControl variant="outlined" size="small">
          <Select
            className={styles.select}
            value={
              listingsState.filterResult.filters.listingSortOptionId ||
              RoomAdvertSortOptionID.NewestFirst
            }
            label={T(
              RoomAdvertSortOptionIDTranslations[
                RoomAdvertSortOptionID.NewestFirst
              ],
            )}
            autoWidth
            size="small"
            input={
              <OutlinedInput
                size="small"
                startAdornment={
                  <InputAdornment position="start">
                    <SortIcon fontSize="small" />
                  </InputAdornment>
                }
              />
            }
            renderValue={renderValue}
            IconComponent={KeyboardArrowDownIcon}
            onChange={handleSortChange}
          >
            {RoomAdvertSortOptionIDValues.map(sortOption => (
              <MenuItem key={sortOption} value={sortOption}>
                <Typography variant={isMobile ? 'body3' : 'body2'}>
                  {T(RoomAdvertSortOptionIDTranslations[sortOption])}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};
export const SortListings = React.memo(SortListingsInternal);
